import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { queryKeys } from '../queryRegistry';
import { Account, AccountFormData, AccountFilters, PaginatedAccounts } from 'types/account';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useContext } from 'react';
import { AuthContext } from 'components/authentication/context/authContext';
import intl from 'components/i18n/ReactIntlWrapper';

// Interface for account type data from database
export interface AccountTypeData {
  id: number;
  name: string;
  description: string;
  labelKey: string | null; // Translation key, can be null
  displayName?: string; // Computed field for UI display
  categoryLabelKey?: string; // AccountCategory labelKey from API
  categoryDisplayName?: string; // Computed category display name
}

// Interface for account category data from database
export interface AccountCategoryData {
  id: number;
  name: string;
  description: string;
  labelKey: string | null; // Translation key, can be null
  displayName?: string; // Computed field for UI display
}

// Import generated API types
import { 
  CreateAccountCommand,
  UpdateAccountCommand, 
  Account as BackendAccount
} from 'axiosApi/models';

interface UseAccountListParams {
  page?: number;
  pageSize?: number;
  filters?: AccountFilters;
  enabled?: boolean;
}

/**
 * Hook to fetch available currencies from the API
 */
export const useCurrencies = () => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: ['currencies'],
    queryFn: async (): Promise<Array<{ id: number; code: string; name: string }>> => {
      const response = await invoiceApi.apiVversionCurrencyAllGet('1', 1, 100);
      
      const currencies = response.data.data?.queryResult || [];
      
      return currencies.map((c: any) => ({
        id: c.id,
        code: c.code, // Use DB code directly
        name: c.name
      }));
    },
    enabled: !!userEntityId,
    staleTime: 300000, // Cache for 5 minutes
  });
};

/**
 * Hook to fetch account types from API - returns raw labelKey for intl.get() translation
 */
export const useAccountTypes = () => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: ['accountTypes'],
    queryFn: async (): Promise<AccountTypeData[]> => {
      const response = await invoiceApi.apiVversionListAccountTypeGet('1');

      const items = response.data.data?.queryResult || [];
      return items.map((i: any) => {
        const labelKey = i.label || i.labelKey; // API usa 'label', pero mantenemos compatibilidad
        const name = i.name || labelKey; // Fallback si no hay name
        const displayName = (labelKey && labelKey.trim()) ? intl.get(labelKey) || labelKey : name;
        
        const categoryLabelKey = i.groupLabel; 
        const categoryDisplayName = (categoryLabelKey && categoryLabelKey.trim()) 
          ? intl.get(categoryLabelKey) || categoryLabelKey 
          : '';
        
        return {
          id: i.id,
          name: name,
          description: i.description,
          labelKey: labelKey,
          displayName: displayName,
          categoryLabelKey: categoryLabelKey,
          categoryDisplayName: categoryDisplayName
        };
      });
    },
    staleTime: 300000, // Cache for 5 minutes
  });
};

/**
 * Hook to fetch account categories from API - returns raw labelKey for intl.get() translation
 */
export const useAccountCategories = () => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: ['accountCategories'],
    queryFn: async (): Promise<AccountCategoryData[]> => {
      const response = await invoiceApi.apiVversionListAccountCategoryGet('1');

      const items = response.data.data?.queryResult || [];
      return items.map((i: any) => {
        const labelKey = i.label || i.labelKey; // API usa 'label', pero mantenemos compatibilidad
        const name = i.name || labelKey; // Fallback si no hay name
        const displayName = (labelKey && labelKey.trim()) ? intl.get(labelKey) || labelKey : name;
        return {
          id: i.id,
          name: name,
          description: i.description,
          labelKey: labelKey,
          displayName: displayName
        };
      });
    },
    staleTime: 300000, // Cache for 5 minutes
  });
};

// Interface for routing type data from database
export interface RoutingTypeData {
  id: number;
  name: string;
  description: string;
  labelKey: string | null; // Translation key, can be null
  displayName?: string; // Computed field for UI display
}

/**
 * Hook to fetch routing types from API - returns raw labelKey for intl.get() translation
 */
export const useRoutingTypes = () => {
  const { invoiceApi }: Api = useApi();

  return useQuery({
    queryKey: ['routingTypes'],
    queryFn: async (): Promise<RoutingTypeData[]> => {
      const response = await invoiceApi.apiVversionListRoutingTypeGet('1');

      const items = response.data.data?.queryResult || [];
      return items.map((i: any) => {
        const labelKey = i.label || i.labelKey; // API usa 'label', pero mantenemos compatibilidad
        const name = i.name || labelKey; // Fallback si no hay name
        const displayName = (labelKey && labelKey.trim()) ? intl.get(labelKey) || labelKey : name;
        return {
          id: i.id,
          name: name,
          description: i.description,
          labelKey: labelKey,
          displayName: displayName
        };
      });
    },
    staleTime: 300000, // Cache for 5 minutes
  });
};

/**
 * Hook to fetch paginated list of accounts with filters
 */
export const useAccountList = ({ page = 1, pageSize = 50, filters, enabled = true }: UseAccountListParams = {}) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;
  const { data: accountTypes = [] } = useAccountTypes();

  const queryResult = useQuery({
    queryKey: queryKeys.accounts(filters),
    queryFn: async (): Promise<PaginatedAccounts> => {
      
      // Build filters array for backend
      const backendFilters: string[] = [];
      
      if (filters?.accountType) {
        backendFilters.push(`accountType:${filters.accountType}`);
      }
      
      if (filters?.category) {
        backendFilters.push(`category:${filters.category}`);
      }
      
      if (filters?.isActive !== undefined) {
        backendFilters.push(`isActive:${filters.isActive}`);
      }
      
      if (filters?.corporateEntityId) {
        backendFilters.push(`corporateEntityId:${filters.corporateEntityId}`);
      }
      
      if (filters?.currency) {
        backendFilters.push(`currency:${filters.currency}`);
      }

      const response = await invoiceApi.apiVversionAccountSearchGet(
        '1', 
        page, 
        pageSize, 
        filters?.search || '', 
        'name', 
        'asc',
        backendFilters.length > 0 ? backendFilters : undefined
      );
      
      // Transform backend response to match frontend Account interface
      const queryResult = response.data.data?.queryResult || [];
      
      const transformedData = queryResult.map((item: BackendAccount) => {
        const accountTypeData = accountTypes.find(type => type.id === item.idAccountType);
        return {
          id: item.id || 0,
          accountNumber: item.accountNumber || '',
          accountName: item.name || '',
          accountType: (accountTypeData?.name || 'Other'), // Keep for backward compatibility
          accountTypeId: item.idAccountType || 1,
          currency: item.currency?.code || 'USD',
          bank: item.bank || '',
          routingNumber: item.routingNumber || '',
          routingTypeId: item.idRoutingType || undefined,
          routingTypeName: item.routingType?.name || '',
          isActive: item.active ?? true,
          corporateEntityId: item.idCorporateEntity || 0,
          corporateEntityName: item.corporateEntity?.name || '',
          personaId: item.idPersona || undefined,
          tenantId: 0,
          description: item.details || '',
        };
      });
      
      return {
        data: transformedData,
        total: response.data.data?.totalRecords || 0,
        page,
        pageSize,
        totalPages: Math.ceil((response.data.data?.totalRecords || 0) / pageSize),
      };
    },
    enabled: enabled && !!userEntityId && accountTypes.length > 0,
  });

  return queryResult;
};

/**
 * Hook to fetch single account by ID
 */
export const useAccount = (id: number, enabled = true) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: queryKeys.account(id),
    queryFn: async (): Promise<Account> => {
      const response = await invoiceApi.apiVversionAccountIdGet(id, '1');
      
      // Transform backend response to match frontend Account interface
      const item = response.data.data;
      
      return {
        id: item?.id || 0,
        accountNumber: item?.accountNumber || '',
        accountName: item?.name || '',
        accountType: 'Other', // Legacy field - use accountTypeId instead  
        accountTypeId: item?.idAccountType || 1,
        currency: item?.currency?.code || 'USD', // Use DB code directly
        bank: item?.bank || '',
        routingNumber: item?.routingNumber || '',
        routingTypeId: item?.idRoutingType || undefined,
        routingTypeName: item?.routingType?.name || '',
        isActive: item?.active ?? true,
        corporateEntityId: item?.idCorporateEntity || 0,
        corporateEntityName: item?.corporateEntity?.name || '',
        personaId: item?.idPersona || undefined,
        tenantId: 0, // tenantId no existe en el backend, usar valor por defecto
        description: item?.details || '',
      };
    },
    enabled: enabled && !!id && !!userEntityId,
  });
};

/**
 * Hook to create new account
 */
export const useCreateAccount = () => {
  const { invoiceApi }: Api = useApi();
  const queryClient = useQueryClient();
  const { success, error } = useToastMessageQueue();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useMutation({
    mutationFn: async (data: AccountFormData): Promise<Account> => {
      // Map frontend data to backend CreateAccountCommand
      const payload: CreateAccountCommand = {
        name: data.accountName,
        accountNumber: data.accountNumber,
        bank: data.bank || '',
        routingNumber: data.routingNumber || '',
        idAccountType: data.accountTypeId,
        idCurrency: data.idCurrency,
        idCorporateEntity: data.corporateEntityId || 0,
        idRoutingType: data.routingTypeId || undefined,
        idPersona: data.personaId || null,
        details: data.description || '',
        active: data.isActive ?? true,
        openingBalance: data.openingBalance || 0,
        userEntityId: userEntityId || 0,
      };
      // Use generated API instead of direct axios call
      const response = await invoiceApi.apiVversionAccountPost('1', payload);
      
      // Transform response to match frontend Account interface
      const item = response.data.data;
      return {
        id: item?.id || 0,
        accountNumber: data.accountNumber,
        accountName: data.accountName,
        accountType: 'Other', // Legacy field - use accountTypeId instead
        accountTypeId: data.accountTypeId,
        currency: item?.currency?.code || 'USD', // Use DB code from response
        bank: data.bank || '',
        routingNumber: data.routingNumber || '',
        routingTypeId: data.routingTypeId || undefined,
        isActive: data.isActive ?? true,
        corporateEntityId: data.corporateEntityId || 0,
        corporateEntityName: '',
        personaId: data.personaId || undefined,
        tenantId: 0,
        description: data.description || '',
      };
    },
    onSuccess: (account, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      success({
        message: intl.get('accountManagement.createSuccess', { accountName: variables.accountName }),
      });
    },
    onError: (err: any) => {
      // Check for validation errors first (array format)
      const validationErrors = err?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const firstError = validationErrors[0];
        // Prefer ErrorLabelKey for internationalization, fallback to ErrorMessage
        const errorKey = firstError.errorLabelKey || firstError.errorMessage;
        const translatedMessage = errorKey ? intl.get(errorKey) : firstError.errorMessage;
        error({
          message: translatedMessage,
        });
      } else {
        // Fallback to generic message or single error format
        error({
          message: err?.response?.data?.message || intl.get('accountManagement.createError'),
        });
      }
    },
  });
};

/**
 * Hook to update existing account
 */
export const useUpdateAccount = () => {
  const { invoiceApi }: Api = useApi();
  const queryClient = useQueryClient();
  const { success, error } = useToastMessageQueue();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AccountFormData }): Promise<Account> => {
      // Map frontend data to backend UpdateAccountCommand - only editable fields
      const payload: UpdateAccountCommand = {
        id: id,
        name: data.accountName,
        idPersona: data.personaId || null,
        details: data.description || '',
        active: data.isActive ?? true,
        userEntityId: userEntityId || 0,
      };
      
      const response = await invoiceApi.apiVversionAccountIdPut(id, '1', payload);
      
      // Transform response to match frontend Account interface
      const item = response.data.data;
      return {
        id: item?.id || 0,
        accountNumber: item?.accountNumber || '',
        accountName: item?.name || '',
        accountType: 'Other', // Legacy field - use accountTypeId instead
        accountTypeId: item?.idAccountType || 1,
        currency: item?.currency?.code || 'USD', // Use DB code from response
        bank: item?.bank || '',
        routingNumber: item?.routingNumber || '',
        routingTypeId: item?.idRoutingType || undefined,
        routingTypeName: item?.routingType?.name || '',
        isActive: item?.active ?? true,
        corporateEntityId: item?.idCorporateEntity || 0,
        corporateEntityName: item?.corporateEntity?.name || '',
        personaId: item?.idPersona || undefined,
        tenantId: 0, // tenantId no existe en el backend, usar valor por defecto
        description: item?.details || '',
      };
    },
    onSuccess: (account, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.account(variables.id) });
      success({
        message: intl.get('accountManagement.updateSuccess', { accountName: variables.data.accountName }),
      });
    },
    onError: (err: any) => {
      // Check for validation errors first (array format)
      const validationErrors = err?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const firstError = validationErrors[0];
        // Prefer ErrorLabelKey for internationalization, fallback to ErrorMessage
        const errorKey = firstError.errorLabelKey || firstError.errorMessage;
        const translatedMessage = errorKey ? intl.get(errorKey) : firstError.errorMessage;
        error({
          message: translatedMessage,
        });
      } else {
        // Fallback to generic message or single error format
        error({
          message: err?.response?.data?.message || intl.get('accountManagement.updateError'),
        });
      }
    },
  });
};

/**
 * Hook to delete account (soft delete)
 */
export const useDeleteAccount = () => {
  const { invoiceApi }: Api = useApi();
  const queryClient = useQueryClient();
  const { success, error } = useToastMessageQueue();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await invoiceApi.apiVversionAccountIdDelete(id, '1');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      success({
        message: intl.get('accountManagement.deleteSuccess'),
      });
    },
    onError: (err: any) => {
      error({
        message: err?.response?.data?.message || intl.get('accountManagement.deleteError'),
      });
    },
  });
};

/**
 * Hook to search accounts (lightweight typeahead)
 */
export const useAccountSearch = (searchTerm: string, enabled = true) => {
  const { invoiceApi }: Api = useApi();

  return useQuery({
    queryKey: ['accounts', 'search', searchTerm],
    queryFn: async (): Promise<Account[]> => {
      if (!searchTerm || searchTerm.length < 2) return [];
      
      const response = await invoiceApi.apiVversionAccountSearchGet('1', 1, 10, searchTerm);
      const queryResult = response.data.data?.queryResult || [];
      
      // Transform backend Account to frontend Account
      return queryResult.map((item: BackendAccount) => ({
        id: item.id || 0,
        accountNumber: item.accountNumber || '',
        accountName: item.name || '',
        accountType: 'Other', // Legacy field - use accountTypeId instead
        accountTypeId: item.idAccountType || 1,
        currency: item.currency?.code || 'USD',
        isActive: item.active ?? true,
        corporateEntityId: item.idCorporateEntity || 0,
        corporateEntityName: item.corporateEntity?.name || '',
        tenantId: 0, // tenantId no existe en el backend, usar valor por defecto
        description: item.details || '',
      }));
    },
    enabled: enabled && searchTerm.length >= 2,
  });
};
